import { ATTEMPT_ORDINAL_START } from 'features/bulk-email/sequences/components/BulkEmailDrawerAttempts/BulkEmailDrawerAttempts.constants';
import strings from 'strings';
import { BulkMenuItems, BulkScopes } from 'UI/constants/defaults';
import { EntityType } from 'UI/constants/entityTypes';
import { getOrdinalSuffix } from 'UI/utils';

import { PREVIEW_BULK_RENDERERS } from './PreviewBulkRenderers';

const { mailMeta: MAIL_META_STRINGS } = strings.bulkEmails.preview;

export const booleanCopyRenderer = active =>
  active ? MAIL_META_STRINGS.booleanOptions.yes : MAIL_META_STRINGS.booleanOptions.no;

const getBlockResendCopy = (blockResend, blockDays) => {
  const blockResendCopy = booleanCopyRenderer(blockResend);
  if (!blockResend) return blockResendCopy;

  const blockDaysCopy = blockDays ? `(${blockDays} Days)` : `(no interval selected)`;

  return `${blockResendCopy} ${blockDaysCopy}`;
};

const buildCommonMainMeta = ({ date, userInfo }) => {
  return {
    from: {
      title: MAIL_META_STRINGS.from.title,
      render: `${userInfo.name} - ${userInfo.email}`,
      enabled: true
    },
    date: {
      title: MAIL_META_STRINGS.date.title,
      render: PREVIEW_BULK_RENDERERS.Date(date),
      enabled: true
    }
  };
};

const checkIfDateHasPassed = sentDate => {
  const today = new Date();
  const sentOnDate = new Date(sentDate);

  return today > sentOnDate;
};

const mapSequencesMetaData = ({ sequences, userInfo }) => {
  if (!sequences) return null;

  const today = new Date();
  const mappedMeta = sequences.map((attempt, index) => {
    const key = getOrdinalSuffix(index + ATTEMPT_ORDINAL_START);
    const commonMeta = buildCommonMainMeta({
      date: attempt.emailHistory.created_at,
      userInfo
    });
    const { scheduledAt: sentOn } = attempt;

    return {
      key,
      bulkId: attempt.emailHistory.id,
      subject: attempt.emailHistory.emailBody.subject,
      html: attempt.emailHistory.emailBody.html,
      shouldEnableMenuButton: new Date(sentOn) > today,
      shouldEnableStatsButton: new Date(sentOn) < today,
      meta: [
        commonMeta.from,
        {
          title: MAIL_META_STRINGS.to.title,
          render: checkIfDateHasPassed(sentOn)
            ? PREVIEW_BULK_RENDERERS.SearchProjects({
                searchProject: {
                  ...attempt.emailHistory.searchProject,
                  bulkableItems: attempt.emailHistory.searchProject.total_items
                }
              })
            : "Search Project created before send with recipients who didn't open the previous bulk",
          enabled: true
        },
        commonMeta.date,
        {
          title: 'Sent on',
          render: PREVIEW_BULK_RENDERERS.Date(sentOn),
          enabled: true
        }
      ]
    };
  });

  return mappedMeta;
};

export const getMailMeta = ({
  bulkInPreviewData,
  isBlockAssociatedCompaniesActive = false,
  isSequencesActive = false,
  selectedMenuItem
}) => {
  const {
    createdAt,
    emailBody: { attachments },
    emailConfig: {
      blockResend,
      blockDays,
      bulkType,
      generalCategory,
      isScheduled,
      marketing: { marketingCandidates, ...marketingConfig },
      recruiting: { recruitingJobOrders, ...recruitingConfig },
      template: { name: templateName }
    },
    searchProject,
    sendDate,
    userInfo
  } = bulkInPreviewData;

  const commonMailMeta = buildCommonMainMeta({
    date: selectedMenuItem === BulkMenuItems.Drafts ? createdAt : sendDate,
    userInfo
  });

  const mainMailMeta = [
    commonMailMeta.from,
    {
      title: MAIL_META_STRINGS.to.title,
      render: PREVIEW_BULK_RENDERERS.SearchProjects({
        searchProject
      }),
      enabled: true
    },
    commonMailMeta.date,
    {
      title: MAIL_META_STRINGS.bulkType.title,
      render: BulkScopes.ById[bulkType?.id] || MAIL_META_STRINGS.bulkType.defaultValue,
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.generalCategory.title,
      render: generalCategory?.title || MAIL_META_STRINGS.generalCategory.defaultValue,
      enabled: bulkType?.id === BulkScopes.General
    },
    {
      title: MAIL_META_STRINGS.candidatesToMarket.title,
      render: PREVIEW_BULK_RENDERERS.BulkTypeEntities({
        entityType: EntityType.Candidate,
        entities: marketingCandidates
      }),
      enabled: bulkType?.id === BulkScopes.Marketing && marketingCandidates?.length > 0
    },
    {
      title: MAIL_META_STRINGS.jobOrdersToRecruit.title,
      render: PREVIEW_BULK_RENDERERS.BulkTypeEntities({
        entityType: EntityType.Joborder,
        entities: recruitingJobOrders
      }),
      enabled: bulkType?.id === BulkScopes.Recruiting && recruitingJobOrders?.length > 0
    },
    {
      title: MAIL_META_STRINGS.isSchedule.title,
      render: booleanCopyRenderer(isScheduled),
      enabled: selectedMenuItem === BulkMenuItems.Sent
    }
  ];

  const marketingConfigMeta = [
    {
      position: 1,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockHirings,
      value: marketingConfig.blockCompanies
    },
    {
      position: 3,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockSignedClientCompanies,
      value: marketingConfig.blockClientCompanies
    },
    {
      position: 4,
      title: MAIL_META_STRINGS.candidatesToMarket.config.allowReachingCandidates,
      value: marketingConfig.allowMarketingCandidates
    }
  ];

  if (isBlockAssociatedCompaniesActive) {
    marketingConfigMeta.push({
      position: 2,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockAssociatedCompanies,
      value: marketingConfig.blockAssociatedCompanies
    });
  }

  const orderedMarketingMeta = [...marketingConfigMeta.sort((a, b) => a.position - b.position)];

  const secondaryMailMeta = [
    ...orderedMarketingMeta.map(meta => ({
      ...meta,
      render: booleanCopyRenderer(meta.value),
      enabled: bulkType?.id === BulkScopes.Marketing
    })),
    {
      title: MAIL_META_STRINGS.jobOrdersToRecruit.config.allowReachingHirings,
      render: booleanCopyRenderer(recruitingConfig.allowRecruitHirings),
      enabled: bulkType?.id === BulkScopes.Recruiting
    },
    {
      title: MAIL_META_STRINGS.template.title,
      render: templateName,
      enabled: !!templateName
    },
    {
      title: MAIL_META_STRINGS.blockResend.title,
      render: getBlockResendCopy(blockResend, blockDays),
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.attachments.title,
      render: PREVIEW_BULK_RENDERERS.Attachments({ attachments }),
      RowProps: {
        alignItems: 'flex-start'
      },
      enabled: attachments.length > 0
    }
  ];

  const sequencesMeta = isSequencesActive
    ? mapSequencesMetaData({
        sequences: bulkInPreviewData?.sequences?.entries,
        userInfo
      })
    : null;

  return { mainMailMeta, secondaryMailMeta, sequencesMeta };
};
